import { gql } from '@apollo/client/core';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { LoadingImage } from 'component-lib/atoms/LoadingImage/LoadingImage';
import { Button } from 'play/atoms/button/Button';
import { ChallengeTimerText } from 'play/legacy/assignment/timer/ChallengeTimerText';
import { HeaderLegacy } from 'play/legacy/organisms/header-legacy/HeaderLegacy';
import { Container, WorkspaceCol } from 'play/legacy/pages/learn-environment-legacy/LearnEnvironmentLegacy';
import { ShadowScrollContainer } from 'play/molecules/shadow-scroll-container/ShadowScrollContainer';
import { Bottom, Top, TopContent } from 'play/organisms/assignment/Assignment';
import { Progress } from 'play/organisms/progress/Progress';
import { getSidebarWidth } from 'play/support/getSidebarWidth';
import { isSkippingEnabled } from 'play/support/isSkippingEnabled';
import { useFinishButtonParameters } from 'play/support/useFinishButtonParameters';
import { useTrackAmplitudeEvent } from 'support/amplitude/amplitudeEvent';
import { Mode, useRecapQuery, useRecapTrackInviteQuery } from 'types/graphql';

import { useNotificationContext } from '../../../core/context/Notifications';
import ErrorMutation from '../../../core/legacy/components/core/Error';
import { getAccessSetting } from '../../../support/accessSetting';
import { getShareURL } from '../../../support/invite';
import {
  AssignmentSidebar,
  DEFAULT_SIDEBAR_OVERLAY,
  DEFAULT_SIDEBAR_POSITION,
  DEFAULT_SIDEBAR_WIDTH,
} from '../../atoms/assignment-sidebar/AssignmentSidebar';
import { LabMetadataContext } from '../../context/LabMetadataContext';
import { ShareButton } from '../../molecules/share-button/ShareButton';
import { TrackReview } from '../../organisms/review/TrackReview';
import { TrackNotFound } from '../../organisms/track-not-found/TrackNotFound';

export interface RecapContainerProps {
  mode: Mode;
  teamSlug: string;
  trackSlug: string;
  inviteID?: string;
}

const RecapContainer = ({ mode, teamSlug, trackSlug, inviteID }: RecapContainerProps) => {
  const history = useHistory();
  const trackAmplitudeEvent = useTrackAmplitudeEvent();
  const { dispatch } = useNotificationContext();
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const { data: inviteData } = useRecapTrackInviteQuery({
    variables: { inviteID: inviteID as string },
    skip: Boolean(!inviteID),
  });
  const { data, loading, error } = useRecapQuery({
    variables: { teamSlug, trackSlug },
  });
  const [finishButtonParamaters] = useFinishButtonParameters();
  const showShareButton = inviteData?.trackInvite?.canClaim && inviteData?.trackInvite?.enableLearnerSharing;

  useEffect(() => {
    setSidebarWidth(getSidebarWidth(data?.track?.lab_config?.width));
  }, [data]);

  const onClickFinish = () => {
    trackAmplitudeEvent({ name: 'Lab - Clicked finish track button' });
  };

  const onClickCustom = () => {
    trackAmplitudeEvent({
      name: 'Lab - Clicked custom finish track button',
      data: { text: finishButtonParamaters?.text ?? '' },
    });
  };

  const onClickShare = async () => {
    try {
      if (!inviteData?.trackInvite) throw new Error('No invite was found. Please try again later.');

      const inviteURL = getShareURL(teamSlug, inviteData.trackInvite.id);

      await navigator.clipboard.writeText(inviteURL);

      trackAmplitudeEvent({
        name: 'Lab - Clicked Share',
        data: {
          from: 'Finish screen',
          invite_access_setting: getAccessSetting({
            allowAnonymous: Boolean(inviteData.trackInvite.allowAnonymous),
            allowedEmailAddressesOnly: Boolean(inviteData.trackInvite.allowedEmailAddresses?.length),
          }),
        },
      });
    } catch (error) {
      dispatch('error', 'Failed to copy link', error instanceof Error ? error.message : '', 5);
    } finally {
      dispatch('success', 'Link copied', 'You can now share this collection of content.', 5);
    }
  };

  if (loading) return <LoadingImage />;
  if (error) {
    if (error.message.includes('Entity not found')) {
      return <TrackNotFound teamSlug={teamSlug} trackSlug={trackSlug} />;
    }

    return <ErrorMutation type="error" title="Error" message={error.message} />;
  }

  // If track is accessed through an invite, we navigate back to the invite overview upon completion. If not, we navigate back to the track detail page.
  const finishURL = inviteID ? `/${teamSlug}/invite/${inviteID}` : data?.track?.permalink || undefined;

  const skippingEnabled = isSkippingEnabled(data);

  return (
    <LabMetadataContext
      mode={mode}
      participantId={data?.track?.participant?.id}
      trackId={data?.track?.id}
      teamSlug={teamSlug}
      trackSkippable={skippingEnabled}
      inviteId={inviteID}
    >
      <Container>
        <Helmet>
          <title>{data?.track?.title ? `${data.track.title} - Recap - instruqt` : 'Recap - instruqt'}</title>
        </Helmet>
        <AssignmentSidebar
          setWidth={setSidebarWidth}
          sidebarOverlay={data?.track?.lab_config?.overlay ?? DEFAULT_SIDEBAR_OVERLAY}
          sidebarPosition={data?.track?.lab_config?.position === 'left' ? 'left' : DEFAULT_SIDEBAR_POSITION}
          width={sidebarWidth}
        >
          <Top>
            <TopContent>
              <div tw="flex flex-1 flex-row justify-end gap-2">
                {data?.track && (
                  <Progress
                    challenges={data.track.challenges}
                    mode={mode}
                    skippingEnabled={skippingEnabled}
                    teamSlug={teamSlug}
                    track={data.track}
                    tw="flex-1"
                    challengeTimer={<ChallengeTimerText tw="ml-1">Completed</ChallengeTimerText>}
                  />
                )}
              </div>
            </TopContent>
          </Top>
          <ShadowScrollContainer tw="rich-text text-white p-3">
            <h2>Great job!</h2>
          </ShadowScrollContainer>
          <Bottom>
            <Buttons tw="justify-end">
              {mode === 'embed' ? (
                finishButtonParamaters && (
                  <a
                    href={finishButtonParamaters.url}
                    onClick={onClickCustom}
                    target={finishButtonParamaters.target}
                    rel="noreferrer"
                  >
                    <Button>{finishButtonParamaters.text}</Button>
                  </a>
                )
              ) : (
                <div tw="w-full flex items-center" css={[showShareButton ? tw`justify-between` : tw`justify-end`]}>
                  {showShareButton && <ShareButton onClick={onClickShare} />}
                  <Link to={{ pathname: finishURL, state: history.location.state }}>
                    <Button onClick={onClickFinish}>Finish</Button>
                  </Link>
                </div>
              )}
            </Buttons>
          </Bottom>
        </AssignmentSidebar>
        <WorkspaceCol>
          <HeaderLegacy hideExpand hideRefresh />

          <DarkOverlay tw="flex justify-center items-center w-full h-full">
            <TrackReview trackID={data?.track?.id ?? ''} teamSlug={teamSlug} />
          </DarkOverlay>
        </WorkspaceCol>
      </Container>
    </LabMetadataContext>
  );
};

export default RecapContainer;

gql`
  query Recap($trackSlug: String!, $teamSlug: String!) {
    track(trackSlug: $trackSlug, teamSlug: $teamSlug) {
      id
      title
      permalink
      ...ProgressTrack
      challenges {
        permalink
        ...ProgressChallenge
      }
      participant {
        id
      }
      lab_config {
        overlay
        position
        width
      }
    }

    ...IsSkippingEnabledQuery
  }
`;

gql`
  query recapTrackInvite($inviteID: String!) {
    trackInvite(inviteID: $inviteID) {
      id
      canClaim
      enableLearnerSharing
      allowAnonymous
      allowedEmailAddresses
    }
  }
`;

const DarkOverlay = styled.div<{ theme: any }>`
  background: ${props => props.theme.colors.blue.dark};
`;

const Buttons = styled.div`
  ${tw`text-base`}
  box-sizing: border-box;
  display: flex;
  height: 60px;
  justify-content: space-between;
  padding: 10px 17px;
`;
